/**
 * Tipos do dominio, espelhados dos schemas Pydantic do backend do manager.
 *
 * Escritos a mao de proposito. Fonte: `backend/app/schemas/`.
 *
 * ATENCAO aos caminhos: os routers ficam sob `/auth`, `/catalog`, `/onboarding` e `/auth-catalog`
 * (ver `backend/app/api/router.py`), e o prefixo da aplicacao e `/api/v1`. Deduzir caminho pelo
 * nome do arquivo de rota custou um 404 em producao no Report Viewer - la o modulo se chama
 * `dashboards.py` e o router esta em `/reports`.
 */

export interface Me {
  id: string
  tenant_id: string
  /** Nome do tenant, resolvido no Auth pelo backend. Ausente quando a busca falha. */
  tenant_name?: string | null
  email: string
  /** O `/me` do manager nao devolve nome de pessoa; fica opcional para a casca ser a mesma. */
  name?: string | null
  status?: string
}

export interface LoginResult {
  access_token: string
  token_type: string
}

/** Contagens do catalogo (`GET /catalog/summary`) - resposta direta, sem envelope `rows`. */
export interface CatalogSummary {
  clients: number
  hospitals: number
  powerbi_connections: number
  datasets: number
  reports: number
  published_reports: number
  access_policies: number
}

/**
 * Rota publicada por um servico do Auth (`/auth-catalog/*-paths`).
 *
 * E uma STRING, nao um objeto: o backend devolve `sorted(openapi["paths"].keys())`. Tratar como
 * objeto fez a tela renderizar uma coluna por CARACTERE do caminho - cada letra numa celula.
 */
export type RotaDoAuth = string

/**
 * Entidades do catalogo. Campos conferidos em `backend/app/services/catalog_service.py`, nos
 * `_*_payload` - e o que a listagem devolve de fato, que nao e o mesmo que o modelo do banco.
 */

/** Espelho de um Tenant do Auth. Nome, slug e status vem de la e nao sao editaveis aqui. */
export interface Client {
  id: string
  name: string
  slug: string
  auth_tenant_id: string
  status: string
}

/** Espelho de uma Organization do Auth (hospital, plano ou area). Idem: espelho, nao original. */
export interface Hospital {
  id: string
  client_id: string
  client_slug: string
  name: string
  slug: string
  auth_organization_id: string
  status: string
}

/**
 * Service principal (Entra) usado para falar com o Power BI.
 *
 * `secret_ref` e uma REFERENCIA (a chave dentro de `POWERBI_CONNECTIONS_JSON`), nao o segredo: o
 * valor real nunca sai do `.env` do backend. Por isso pode aparecer na tela.
 */
export interface PowerBiConnection {
  id: string
  client_id: string
  client_slug: string
  name: string
  connection_key: string
  microsoft_tenant_id: string
  entra_client_id: string
  secret_ref: string
  is_active: boolean
}

/** Semantic model do Power BI: e nele que moram os dados e as roles de RLS. */
export interface Dataset {
  id: string
  client_id: string
  client_slug: string
  name: string
  description: string | null
  powerbi_connection_id: string | null
  powerbi_dataset_id: string
  powerbi_workspace_id: string
  is_active: boolean
}

/** O que impede excluir um registro (`GET /catalog/{entidade}/{id}/dependencies`). */
export interface Dependencia {
  kind: string
  label: string
  count: number
}

export interface Dependencias {
  id: string
  entity: string
  blocked: boolean
  dependencies: Dependencia[]
}

/**
 * Relatorio do catalogo. So os campos que as telas usam - o payload do backend tem mais, e os
 * detalhes mostram o objeto inteiro de qualquer forma.
 *
 * `auth_workspace_id` e a PORTA DE ACESSO: quem nao alcanca esse workspace no Auth nao ve o
 * relatorio, independentemente de estar publicado.
 */
export interface Report {
  id: string
  client_id: string
  client_slug: string
  hospital_id: string | null
  hospital_slug: string | null
  auth_workspace_id: string
  report_type: string
  name: string
  description: string | null
  category: string | null
  dataset_id: string | null
  powerbi_connection_id: string | null
  powerbi_workspace_id: string | null
  powerbi_report_id: string | null
  embed_url: string | null
  report_url: string | null
  publication_status: string
  is_active: boolean
}

/** Estados de publicacao aceitos pelo backend (`ReportCreate.publication_status`). */
export const ESTADOS_DE_PUBLICACAO = ['draft', 'published', 'archived'] as const

/** Regra de uma policy. Hoje so o `role_name` e usado no RLS do Power BI. */
export interface AccessRule {
  id?: string
  role_name?: string | null
  target_table?: string | null
  target_column?: string | null
  operator?: string | null
  value_type?: string | null
  values?: string[]
  dynamic_source?: string | null
  page_name?: string | null
}

/**
 * Politica de acesso: liga relatorio + profile do Auth + role(s) do semantic model.
 *
 * `identity_source` e o que vai como `username` no token de embed - e o que a role DAX compara com
 * `USERNAME()`. `custom_data_source`, quando preenchido, vira `CUSTOMDATA()` (spec 0005).
 */
export interface AccessPolicy {
  id: string
  client_id: string
  client_slug: string
  report_id: string
  dataset_id: string | null
  auth_profile_id: string
  name: string
  description: string | null
  policy_type: string
  identity_source: string | null
  custom_data_source: string | null
  is_active: boolean
  rules: AccessRule[]
}

/**
 * Resources globais deste app no Auth (spec 0009). As chaves sao as mesmas do backend
 * (`backend/app/api/dependencies.py`); divergir aqui nao da erro de compilacao, da uma tela que
 * esconde a coisa errada.
 */
export const RESOURCE_CATALOGO = 'report-manager-catalog'
export const RESOURCE_CONEXOES = 'report-manager-powerbi-connections'
export const RESOURCE_POLICIES = 'report-manager-rls-policies'
export const RESOURCE_DISCOVERY = 'report-manager-powerbi-discovery'
export const RESOURCE_DIAGNOSTICO = 'report-manager-auth-diagnostics'

export interface Capacidade {
  read: boolean
  manage: boolean
}

/**
 * O que a sessao alcanca, por modulo. **Isto e visibilidade, nao autorizacao**: quem autoriza e o
 * backend, em cada rota. Esconder um botao sem a checagem no servidor seria so deixar o caminho
 * aberto para quem chamar a API direto.
 *
 * `module_permissions_enforced` falso = o Auth ainda nao tem os resources de modulo registrados, e
 * o backend responde "pode tudo" para quem passou pelo portao unico - o comportamento de sempre.
 */
export interface Permissoes {
  module_permissions_enforced: boolean
  modules: Record<string, Capacidade | undefined>
}
